<template>
  <div class="sm-billing-info">
    <div class="sm-wrap sm">
      <div class="sm-billing-info-inner">
        <template v-if="hasPaymentGateway">
          <h2 class="sm-txt-center">Get started</h2>
          <!-- <p class="sm-lead sm-txt-center">Cancel anytime</p> -->
          <div class="sm-billing-info__content">
            <div class="sm-billing-info__content-main">
              <div class="sm-billing-info__content-item">
                <div class="sm-billing-info__content-item-flex">
                  <div>
                    <h4>Your plan</h4>
                    <p class="sm-lead">{{userPlan.title}}</p>
                  </div>
                  <span v-if="period == 'monthly'">{{formatPrice(userPlan.subscription_price)}}/month</span>
                  <span v-else>{{formatPrice(userPlan.period_amount)}}/{{periodAlts[userPlan.period_unit]}}</span>
                  <a href="#" @click.prevent="$router.push('/select-plan')">Change plan</a>
                </div>
                <div class="sm-billing-info__content-item-delimiter"></div>

                <div
                  v-if="coupon.show"
                  class="sm-billing-info__content-item-block delimiter"
                  :class="coupon.applied ? 'overlay' : ''"
                >
                  <div 
                    v-if="!coupon.applied"
                    class="sm-billing-info__content-item-block-coupon"
                  >
                    <span>Coupon code</span>
                    <input-text
                      v-model="coupon.code"
                      :name="'coupon'"
                      :error="coupon.error"
                    />
                    <btn-block
                      :type="'green-line'"
                      :text="'Apply code'"
                      @click="addCoupon"
                    />
                  </div>
                  <div 
                    v-else
                    class="sm-billing-info__content-item-block-coupon"
                  >
                    <a
                      href="#"
                      class="sm-grey-link"
                      @click.prevent="removeCoupon"
                    >Remove</a>
                    <span class="text">{{coupon.text}}</span>
                  </div>
                </div>
                <div 
                  v-if="providerAddress.line_1"
                  class="sm-billing-info__content-item-block"
                >
                  <h4>Virtual Address</h4>
                  <div class="sm-billing-info__content-item-block-address">
                    <div>
                      <include-svg :name="'icon-location'" />
                      <p>{{providerAddress.line_1}}</p>
                      <p>
                        <template v-if="providerAddress.line_2">{{providerAddress.line_2}}</template>
                        <template v-if="providerAddress.line_3">, {{providerAddress.line_3}}</template>
                      </p>
                    </div>
                    <div>
                      <include-svg :name="'icon-mailbox'" />
                      <p>Mailbox (Unit)</p>
                      <p class="sm-small sm-txt-nowrap disabled">
                        <template v-if="selectedPMB && selectedPMB.item">#{{selectedPMB.item.id}}</template>
                        <template v-else>Automatically generated</template>
                      </p>
                    </div>
                  </div>
                </div>

                <select-mailbox
                  v-if="dataReceived"
                  v-model="selectedPMB"
                />

                <div 
                  v-if="agentService.show"
                  class="sm-billing-info__content-item-delimiter"
                ></div>
                <div
                  v-if="agentService.show"
                  class="sm-billing-info__content-item-block delimiter"
                >
                  <h4>Registered Agent service</h4>
                  <div class="sm-billing-info__content-item-block-switch">
                    <div>
                      <h6>
                        <template v-if="!isMobile">I’d like to enable Registered Agent service</template>
                        <template v-else>Enable Registered Agent service</template>
                      </h6>
                      <span>for {{formatPrice(userPlan.registered_agent_service_price)}}/month</span>
                    </div>
                    <label class="sm-switcher">
                      <input 
                        v-model="agentService.selected"
                        type="checkbox" 
                      />
                      <span></span>
                    </label>
                  </div>
                </div>
              </div>
              <div class="sm-billing-info__content-item spaced">
                <payment-details
                  :initAddress="address"
                  :initCard="card"
                  :account="userAccount"
                  :creditCards="[]"
                  :bankAccount="bankAccount"
                  @address="(e) => {address = e}"
                  @card="(e) => {card = e}"
                  @cardState="(e) => {cardState = e}"
                  @bankData="(e) => {bankData = e}"
                />
              </div>
            </div>
            <div class="sm-billing-info__content-right">
              <div class="sm-billing-info__content-item">
                <h4>Billing</h4>
                <dropdown-block
                  v-model="period"
                  class="sm-dropdown sm-txt-capitalize"
                  :class="periods.length < 2 ? 'disabled' : ''"
                  :options="periods"
                  :searchable="false"
                  :allow-empty="false"
                >
                  <template slot="placeholder">
                    <span>Select period</span>
                    <include-svg :name="'icon-arrow-b'" />
                  </template>
                  <template slot="singleLabel" slot-scope="props">
                    <span v-if="props.option">{{props.option.replace(/_/g, '')}}</span>
                    <include-svg :name="'icon-arrow-b'" />
                  </template>
                  <template slot="option" slot-scope="props" :value="props.option">
                    <span v-if="props.option">{{props.option.replace(/_/g, '')}}</span>
                  </template>
                </dropdown-block>

                <!-- TOP (plan payments) -->
                <template v-if="period == 'monthly'">
                  <div 
                    class="sm-billing-info__content-item-row"
                    :class="{'separate': userPlan.has_prorate}"
                  >
                    <span>Period payment</span>
                    <span>{{formatPrice(userPlan.subscription_price)}}</span>
                  </div>
                  <div 
                    v-if="userPlan.has_prorate"
                    class="sm-billing-info__content-item-row"
                  >
                    <span>Prorated amount</span>
                    <span>{{formatPrice(monthlyAmount)}}</span>
                  </div>
                </template>
                <template v-else>
                  <template v-if="unitSavings > 0">
                    <div class="sm-billing-info__content-item-row">
                      <span>Monthly payment x {{periodLength}}</span>
                      <span>{{formatPrice(userPlan.subscription_price * periodLength)}}</span>
                    </div>
                    <div class="sm-billing-info__content-item-row sm-green separate">
                      <span>Period savings</span>
                      <span>-{{formatPrice(unitSavings)}}</span>
                    </div>
                  </template>
                  <div class="sm-billing-info__content-item-row">
                    <span>Period payment</span>
                    <span>{{formatPrice(userPlan.period_amount)}}</span>
                  </div>
                </template>

                <!-- MIDDLE (optional services/fees) -->
                <div
                  v-if="userPlan.setup_fee"
                  class="sm-billing-info__content-item-row"
                >
                  <span>One-time Activation Fee</span>
                  <span>{{formatPrice(userPlan.setup_fee)}}</span>
                </div>
                <div
                  v-if="userPlan.facility_security_deposit"
                  class="sm-billing-info__content-item-row"
                >
                  <span>Facility Security Deposit</span>
                  <span>{{formatPrice(userPlan.facility_security_deposit)}}</span>
                </div>
                <div 
                  v-if="selectedPMB && selectedPMB.item"
                  class="sm-billing-info__content-item-row"
                >
                  <span>
                    <template v-if="selectedPMB.type == 'old'">Retrieved mailbox number</template>
                    <template v-else>Customized mailbox</template>
                  </span>
                  <span>{{formatPrice(selectedPMB.price)}}</span>
                </div>
                <div
                  v-if="agentService.show && agentService.selected"
                  class="sm-billing-info__content-item-row"
                >
                  <span>Registered Agent service{{userPlan.has_prorate ? ' (prorated)' : ''}}</span>
                  <span>{{formatPrice(agentService.price)}}</span>
                </div>

                <!-- BOTTOM (coupon + total) -->
                <template v-if="period == 'monthly'">
                  <template v-if="provider.country_id != 1">
                    <div 
                      v-for="tax in taxes"
                      :key="tax.id"
                      class="sm-billing-info__content-item-row"
                    >
                      <span>{{tax.name}}, {{tax.rate}}%</span>
                      <span>{{formatPrice((monthlyAmount + optionalServicesTotal) * tax.rate / 100)}}</span>
                    </div>
                  </template>
                  <div
                    v-if="coupon.applied && monthlyDiscount"
                    class="sm-billing-info__content-item-row sm-green"
                  >
                    <span>Discount ({{coupon.amount ? formatPrice(coupon.amount) : `${coupon.percent}%`}})</span>
                    <span>-{{formatPrice(monthlyDiscount - monthlyAccessDiscount)}}</span>
                  </div>
                  <div class="sm-billing-info__content-item-row total">
                    <h4>Total to pay now</h4>
                    <h4>{{formatPrice(monthlyToPay)}}</h4>
                  </div>

                  <span
                    v-if="monthlyAccessDiscount"
                    class="sm-tiny"
                  >* The remaining discount of {{formatPrice(monthlyAccessDiscount)}} will be carried out to the next month</span>
                </template>
                <template v-else>
                  <template v-if="provider.country_id != 1">
                    <div 
                      v-for="tax in taxes"
                      :key="tax.id"
                      class="sm-billing-info__content-item-row"
                    >
                      <span>{{tax.name}} {{tax.rate}}%</span>
                      <span>{{formatPrice((userPlan.period_amount + optionalServicesTotal) * tax.rate / 100)}}</span>
                    </div>
                  </template>
                  <div
                    v-if="coupon.applied && unitDiscount"
                    class="sm-billing-info__content-item-row sm-green"
                  >
                    <span>Discount ({{coupon.amount ? formatPrice(coupon.amount) : `${coupon.percent}%`}})</span>
                    <span>-{{formatPrice(unitDiscount - unitAccessDiscount)}}</span>
                  </div>
                  <div class="sm-billing-info__content-item-row total">
                    <h4>Total to pay now</h4>
                    <h4>{{formatPrice(unitToPay)}}</h4>
                  </div>

                  <span
                    v-if="unitAccessDiscount"
                    class="sm-tiny"
                  >* The remaining discount of {{formatPrice(unitAccessDiscount)}} will be carried out to the next month</span>
                </template>
                
                <btn-block 
                  :text="'Start now'" 
                  :type="`submit`" 
                  :class="{'disabled': isSubmitDisabled }" 
                  @click="submitForm"
                />

                <span
                  v-if="renewalDate" 
                  class="sm-billing-info__content-item-renew sm-tiny disabled"
                >Your subscription will be automatically renewed on {{renewalDate | moment('MMM D, YYYY')}}</span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <include-svg :name="'icon-check-big'" />
          <h2 class="sm-txt-center">Registration successful!</h2>
          <p class="sm-lead sm-txt-center">We will contact you shortly on your email <span class="sm-blue">{{userAccount.email}}</span></p>
          <div class="sm-billing-info__info">
            <div class="sm-billing-info__info-item">
              <h4>Virtual address</h4>
              <div class="sm-lead">
                <p>{{providerAddress.line_1}}</p>
                <p v-if="providerAddress.line_2">{{providerAddress.line_2}}</p>
                <p v-if="providerAddress.line_3">{{providerAddress.line_3}}</p>
              </div>
            </div>
            <div
              v-if="selectedPMB && selectedPMB.type == 'new' && selectedPMB.item"
              class="sm-billing-info__info-item"
            >
              <h4>Mailbox number (Unit)</h4>
              <p class="sm-lead">#{{selectedPMB.item.id}}</p>
            </div>
          </div>
        </template>
      </div>
    </div>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      :class="''"
      :size="popup.type == 'securePayment' ? 'large' : ''"
      @close="() => connectCallback({skipHostedInvoice: true})"
    >
      <template slot="popupContent">
        <confirm-block
          v-if="popup.type == 'securePayment'"
          :title="`Sorry, Payment didn't go through!`"
          :text="``"
          :confirmText="`3D Secure payment link`"
          :confirmType="''"
          :cancelText="`Cancel`"
          @confirm="openLink({
            url: userAccount.pending_invoices[0].hosted_invoice_url, 
            w: 550, 
            h: 800 
          })"
          @cancel="() => connectCallback({skipHostedInvoice: true})"
        >
          <template slot="content">
            <p>Click on 3D Secure payment link to complete payment or cancel and update billing information</p>
          </template>
        </confirm-block>
      </template>
    </popup-block>
    <popup-block
      v-if="popupError.isOpen"
      :open="popupError.isOpen"
      @close="clearPopupError">
      <template slot="popupContent">
        <error-block @close="clearPopupError">
          <slot slot="content">
            <p class="sm-txt-center sm-red">{{popupError.text}}</p>
          </slot>
        </error-block>
      </template>
    </popup-block>
    <sm-spinner :show="showSpinner" :isGlobal="true"/>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import BtnBlock from '@/components/Buttons';
import InputText from '@/components/InputText';
import CreditCard from '@/components/credit-card/CreditCard';
import IncludeFlag from '@/components/IncludeFlag';
import Spinner from '@/components/Spinner';
import FormBilling from '@/components/forms/FormBilling';

import Stripe from '@/mixins/Stripe';
import ProviderAddress from '@/mixins/ProviderAddress';
import {FormatPrice, FormatYear, CalculateProrate} from '@/utils/general-data';
import {OpenLinkInPopup} from '@/utils/url';
import PeriodsSettings from '@/settings/periods';
import Multiselect from 'vue-multiselect';
import PaymentDetails from '@/views/page-elements/PaymentDetails';

import SelectMailbox from '@/views/page-elements/SelectMailbox';
import Bus from '@/utils/bus';
import Popup from '@/components/Popup';
import Confirm from '@/components/Confirm';

export default {
  name: 'billing-info',
  components: {
    'btn-block': BtnBlock,
    'input-text': InputText,
    'credit-card': CreditCard,
    'include-flag': IncludeFlag,
    'sm-spinner': Spinner,
    'form-billing': FormBilling,
    'dropdown-block': Multiselect,
    'select-mailbox': SelectMailbox,
    'payment-details': PaymentDetails,
    'popup-block': Popup,
    'confirm-block': Confirm,
  },
  data() {
    return {
      period: 'monthly',
      periodLength: 12,
      periods: ['monthly'],
      periodAlts: PeriodsSettings.alts,
      card: {
        number: '',
        expiration: {
          month: '',
          year: ''
        },
        cvc: ''
      },
      address: {
        first_name: '',
        last_name: '',
        name: '',
        country: '',
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      showSpinner: false,
      coupon: {
        show: false,
        code: '',
        text: 'Coupon Text',
        applied: false,
        amount: null,
        percent: null,
        error: null
      },
      agentService: {
        show: false,
        selected: false,
        price: 0
      },
      monthlyAccessDiscount: 0,
      unitAccessDiscount: 0,
      hasPaymentGateway: false,
      taxes: [],

      selectedPMB: null,
      dataReceived: false,
      cardState: true,
      bankData: null,
      popup: {
        type: null,
        isOpen: false,
      },
    };
  },
  created() {
    this.showSpinner = true;
    Promise.all([
      this.getUserData(),
      this.getUserPlan(true),
      this.getProvider()
    ])
      .then(([user, plan, provider]) => {
        this.dataReceived = true;
        window.scrollTo(0, 0);

        if (this.provider.payment_gateway)
          this.hasPaymentGateway = true;

        if (this.userAccount.activated_on && this.userAccount.activated_on.length) {
          if (!this.isDev) {
            // Google Analytics: Event snippet for Subscriptions conversion page
            let value = (sessionStorage.getItem('transactionValue') / 100).toFixed(2);
            
            if (value) {
              gtag('event', 'conversion', {
                'send_to': 'AW-823673197/SborCNOCzHoQ7YLhiAM',
                'value': value,
                'transaction_id': ''
              });
  
              ga('send', 'event', 'Purchases', 'Successful', this.provider.name, value);
            }
          }
          
          if (!this.isAdmin || !localStorage.getItem('admin-access'))
            this.$router.push(`/${plan.phone_service_enabled ? '?select_phone=1' : ''}`);
          else
            this.switchToAdmin();
        } else {
          if (this.userPlan.registered_agent_service_enabled) {
            this.agentService.show = true;
            this.agentService.price = this.userPlan.has_prorate ? this.calculateProrate(this.userPlan.registered_agent_service_price) : this.userPlan.registered_agent_service_price;
          }

          if (this.userAccount.id) {
            this.address.first_name = this.userAccount.first_name;
            this.address.last_name = this.userAccount.last_name;
            this.address.street = this.userAccount.street;
            this.address.city = this.userAccount.city;
            this.address.zip = this.userAccount.zip_code;
          }

          if (this.userAccount.term && this.userPlan.period_unit) {
            this.period = this.userAccount.term;
            this.periods.push(this.userPlan.period_unit);
            this.periodLength = this.userPlan.period_unit == 'yearly' ? 12 : 6;
          }

          this.coupon.show = this.userPlan.has_coupons;

          if (this.userAccount.country_id != 1 && this.provider.country_id != 1) { // Taxes for non-US clients only
            let countryData = {
              country_id: this.userAccount.country_id
            };

            if (this.userAccount.state_id)
              countryData.state_id = this.userAccount.state_id;

            this.getTaxesList(countryData)
              .then(res => {
                if (res && res.length)
                  this.taxes = res;
                
                this.showSpinner = false;
              });
          } else {
            this.showSpinner = false;
          }

          if (this.provider.client_level == 3)
            this.getBankInfo();
        }
      })
      .catch(e => this.$router.push('/login'));
  },
  computed: {
    ...mapGetters({
      isDev: 'isDev',
      userAccount: 'account/userAccount',
      userPlan: 'account/userPlan',
      isMobile: 'isMobile',
      provider: 'provider',
      idAdmin: 'isAdmin',
      bankAccount: 'billing/bankAccount',
      popupError: 'popupError',
    }),
    isSubmitDisabled() {
      if (this.cardState)
        return (
          Object.values(this.address).some(item => !item) ||
          Object.values(this.card).some(item => !item) ||
          !this.card.expiration.month ||
          !this.card.expiration.year ||
          (this.address.country.states.length && (!this.address.state || !this.address.state.code)) ||
          this.errors.items.length
        );
      else
        return (
          !this.bankData ||
          !this.bankData.plaid_public_token ||
          !this.bankData.plaid_account_id
        )
    },
    optionalServicesTotal() {
      let res = 0;

      if (this.userPlan.setup_fee)
        res += this.userPlan.setup_fee;

      if (this.userPlan.facility_security_deposit)
        res += this.userPlan.facility_security_deposit;

      if (this.selectedPMB && this.selectedPMB.item)
        res += this.selectedPMB.price;

      if (this.agentService.show && this.agentService.selected)
        res += this.agentService.price;

      return res;
    },
    unitSavings() {
      return this.userPlan.subscription_price * this.periodLength - this.userPlan.period_amount;
    },
    unitDiscount() {
      if (this.coupon.amount)
        return this.coupon.amount;
      else if (this.coupon.percent)
        return (this.userPlan.period_amount + this.optionalServicesTotal + this.unitTaxes) * this.coupon.percent / 100;
        //return this.userPlan.period_amount * this.coupon.percent / 100;
      else
        return 0;
    },
    unitTaxes() {
      let res = 0;

      if (this.provider.country_id == 1)
        return res;

      this.taxes.forEach(tax => {
        res += (this.userPlan.period_amount + this.optionalServicesTotal) * tax.rate / 100;
      });

      return res;
    },
    unitToPay() {
      let res = this.userPlan.period_amount + this.optionalServicesTotal + this.unitTaxes;

      if (this.coupon.applied)
        res -= this.unitDiscount;

      if (res < 0) {
        if (this.coupon.applied)
          this.unitAccessDiscount = Math.abs(res);

        res = 0;
      } else {
        this.unitAccessDiscount = 0;
      }

      return res;
    },
    monthlyAmount() {
      return this.userPlan.has_prorate ? this.calculateProrate(this.userPlan.subscription_price) : this.userPlan.subscription_price;
    },
    monthlyDiscount() {
      if (this.coupon.amount) {
        return this.coupon.amount;
      } else if (this.coupon.percent) {
        // Math.floor is to match backend logic
        return Math.floor((this.monthlyAmount + this.optionalServicesTotal + this.monthlyTaxes) * this.coupon.percent / 100);
      } else {
        return 0;
      }
    },
    monthlyTaxes() {
      let res = 0;

      if (this.provider.country_id == 1)
        return res;

      this.taxes.forEach(tax => {
        res += (this.monthlyAmount + this.optionalServicesTotal) * tax.rate / 100;
      });

      return res;
    },
    monthlyToPay() {
      let res = this.monthlyAmount + this.optionalServicesTotal + this.monthlyTaxes;

      if (this.coupon.applied)
        res -= this.monthlyDiscount;

      if (res < 0) {
        if (this.coupon.applied)
          this.monthlyAccessDiscount = Math.abs(res);

        res = 0;
      } else {
        this.monthlyAccessDiscount = 0;
      }

      return res;
    },
    renewalDate() {
      let today = new Date();
      let res = '';
      
      switch(this.period) {
        case 'monthly':
          res = new Date(today.setMonth(today.getMonth() + 1));
          break;
        case 'semi_annually':
          res = new Date(today.setMonth(today.getMonth() + 6));
          break;
        case 'yearly':
          res = new Date(today.setFullYear(today.getFullYear() + 1));
          break;
        default:
          break;
      }

      return res;
    },
  },
  mixins: [Stripe, ProviderAddress],
  methods: {
    ...mapMutations({
      setPopupError: 'SET_POPUP_ERROR',
      clearPopupError: 'CLEAR_POPUP_ERROR',
    }),
    ...mapActions({
      addCard: 'account/addCard',
      getUserData: 'account/userData',
      getUserPlan: 'account/getUserPlan',
      getProvider: 'getProvider',
      showGlobalNotification: 'showGlobalNotification',
      subscriptionToPlan: 'account/subscriptionToPlan',
      applyCoupon: 'billing/applyCoupon',
      customUserAction: 'account/customUserAction',
      connectToSpreedly: 'billing/connectToSpreedly',
      getTaxesList: 'getTaxesList',
      setIsAdmin: 'setIsAdmin',
      getBankInfo: 'billing/getBankInfo',
      createBankAccount: 'billing/createBankAccount',
    }),
    formatPrice: FormatPrice,
    formatYear: FormatYear,
    calculateProrate: CalculateProrate,
    stripeTokenHandler(status, result) {
      if (result.error) {
        this.showSpinner = false;
        this.showGlobalNotification({
          type: 'error',
          value: [result.error.message, result.error.doc_url],
        });
        
        // this.errors.add({ 
        //   field: 'credit_card',
        //   msg: result.error.message,
        // });
      } else {
        this.submitToDatabase(result);
      }
    },
    submitToDatabase(result) {
      const data = {
        stripe_token: result.id,
        brand: result.card.brand
      };

      this.addCard(data)
        .then(res => {
          this.connectCallback();
        })
        .catch((e) => {
          this.showSpinner = false;
          if (e.response)
            this.showGlobalNotification({
              type: 'error',
              value: ['Subscription error', `Please provide following message to our support: '${e.response.data.errors.base}'`]
            });

          // TEMP
          //this.connectCallback();
        });
    },
    submitForm() {
      this.$validator.validate().then(valid => {
        if (!valid) 
          return false;

        this.showSpinner = true;

        let res = null;
        let prom = new Promise((resolve, reject) => {
          res = resolve;
        });

        Bus.$emit('process-pmb', res);

        prom.then(() => {
          this.processAgentService();  
        });
      });
    },
    processAgentService() {
      if (this.agentService.show && this.agentService.selected)
        this.customUserAction({
          method: 'POST',
          action: 'registered_agent_service'
        })
          .then(res => {
            this.processDataChanges();
          })
          .catch(e => {
            console.log('reg agent error', e, e.response);

            // TEMP
            this.processDataChanges();
          });
      else
        this.processDataChanges();
    },
    processDataChanges() {
      if (this.userAccount.term != this.period || this.coupon.applied) {
        let toSend = {
          plan_id: this.userPlan.id,
          term: this.period
        };

        if (this.coupon.applied)
          toSend.coupon_code = this.coupon.code;

        this.subscriptionToPlan(toSend)
          .then(() => {
            this.connectToPaymentSystem();
          })
          .catch(e => {
            console.log('err on change plan', e, e.response);

            this.connectToPaymentSystem();
          })
      } else {
        this.connectToPaymentSystem();
      }
    },
    async connectToPaymentSystem() {
      try {
        if (this.provider.client_level != 3 || this.cardState) { // Credit card
          if (this.provider.payment_gateway == 'stripe') { // Stripe
            this.handlerSubmitNew();
          } else { // Spreedly or other payment gateway
            await this.connectToSpreedly({
              number: this.card.number,
              year: this.formatYear(this.card.expiration.year),
              month: this.card.expiration.month,
              verification_value: this.card.cvc,
              first_name: this.address.first_name,
              last_name: this.address.last_name,
              address1: this.address.street,
              address2: null,
              city: this.address.city,
              zip: this.address.zip,
              state: this.address.state ? this.address.state.code : 'AL',
              country: this.address.country ? this.address.country.code :'US'
            })
            // await this.connectCallback();
          }
        } else { // Bank account
          this.createBankAccount(this.bankData)
        }
        await this.connectCallback();
      } catch(e) {
        console.log('connectToPaymentSystem Error', e.response);
        this.setPopupError({
          text: e.response.data.errors.base[0],
        })
      } finally {
        this.showSpinner = false;
      }
    },
    connectCallback(obj) {
      let skipHostedInvoice = obj ? obj.skipHostedInvoice : false;
      let pendingInvoices = this.userAccount.pending_invoices;
      this.showSpinner= true;

      sessionStorage.setItem('transactionValue', this.period == 'monthly' ? this.monthlyToPay : this.unitToPay);

      let i = 0;
      let wait = setInterval(() => {
        if (i < 12) {
          i++;
          if (this.userAccount.activated_on && this.userAccount.activated_on.length && this.userAccount.plan_id) {
            clearInterval(wait);
            window.location.reload();
          } else if(!skipHostedInvoice && pendingInvoices && pendingInvoices.length && pendingInvoices[0].hosted_invoice_url) {
            this.showSpinner = false;
            clearInterval(wait);
            this.popup.type = 'securePayment';
            this.popup.isOpen = true;
          } else {
            this.getUserData();
          }
        } else {
          clearInterval(wait);
          window.location.reload();
        }
      }, 500);
    },
    addCoupon() {
      if (!this.coupon.code)
        return;

      this.applyCoupon({
        code: this.coupon.code,
        plan_id: this.userPlan.id
      })
        .then(res => {
          this.coupon.error;
          this.coupon.amount = res.coupon.discount_amount || null;
          this.coupon.percent = res.coupon.discount_percent || null;

          if (this.coupon.amount || this.coupon.percent) {
            this.coupon.applied = true;
            this.coupon.text = this.coupon.code;
          }
        }).
        catch(e => {
          if (e.response && e.response.data)
            this.coupon.error = e.response.data.errors || null;
        });
    },
    removeCoupon() {
      // TEMP
      this.coupon.applied = false;
    },
    switchToAdmin() {
      let adminAccessObj = JSON.parse(atob(localStorage.getItem('admin-access')));
      this.setIsAdmin(false);
      localStorage.removeItem('admin-access');
      this.$router.push(`/admin/customers?access_token=${adminAccessObj.token}&refresh_token=${adminAccessObj.refresh_token}`);
    },
    openLink: OpenLinkInPopup
  },
  watch: {
    card: {
      handler() {
        this.errors.remove('credit_card');
      },
      deep: true
    },
    'coupon.code'(val) {
      if (val)
        this.coupon.error = null;
    },
    'address.first_name'() {
      this.address.name = `${this.address.first_name} ${this.address.last_name}`;
    },
    'address.last_name'() {
      this.address.name = `${this.address.first_name} ${this.address.last_name}`;
    }
  }
};
</script>

<style lang="scss">
@use '@/assets/scss/general/mixins.scss' as *;
@use '@/assets/scss/framework/scss-variables.scss' as *;
@use '@/assets/scss/auth/_billing-info.scss';

.fade-text-enter,
.fade-text-leave-to {
  opacity: 0 !important;
  transform: translateY(30px);
}

.fade-text-enter-active,
.fade-text-leave-active {
  transition: transform 0.2s, opacity 0.2s;
}
</style>
